/**
 * Fonte única do balanceamento de progressão e encontros do IdleMon.
 * Este módulo é puro para poder ser usado tanto pelo navegador quanto pelas auditorias Node.
 */

import { isAquaticOnly } from './fishing-balance.mjs';

export const ROUTE_TYPES = Object.freeze([
  'elétrico', 'fogo', 'água', 'planta', 'inseto', 'voador', 'normal',
  'psíquico', 'pedra', 'terra', 'fantasma', 'dragão', 'lutador',
  'veneno', 'sombrio', 'aço', 'gelo', 'fada',
]);

/**
 * Tipos com elenco amplo o bastante para sustentar uma progressão completa.
 * Tipos de catálogo curto (Dragão, Fada, Fantasma etc.) podem ter lacunas
 * intencionais, que a UI simplesmente omite.
 */
export const FULL_HUNT_PROGRESSION_TYPES = Object.freeze([
  'elétrico', 'fogo', 'água', 'planta', 'inseto', 'voador', 'normal',
  'psíquico', 'pedra', 'terra', 'lutador', 'veneno',
]);

export const ROUTE_LEVEL_RANGES = Object.freeze([
  Object.freeze([1, 1]),
  Object.freeze([10, 10]),
  Object.freeze([20, 20]),
  Object.freeze([40, 40]),
  Object.freeze([60, 60]),
  Object.freeze([80, 80]),
  Object.freeze([100, 100]),
  Object.freeze([150, 150]),
  Object.freeze([200, 200]),
  Object.freeze([250, 250]),
  Object.freeze([300, 300]),
  Object.freeze([350, 350]),
  Object.freeze([400, 400]),
  Object.freeze([450, 450]),
  Object.freeze([500, 500]),
]);

/** Faixas a partir desta âncora usam hunt de zona (packs + AoE), não espécie. */
export const ZONE_HUNT_MIN_LEVEL = 300;

/**
 * Todas as faixas ficam abertas desde o início. O nível da rota passa a ser
 * uma escolha de risco/recompensa, não uma trava de treinador. O tipo Dragão
 * mantém uma progressão própria a partir do Nv.60 para proteger os pseudos.
 */
export const ROUTE_UNLOCK_LEVELS = Object.freeze([
  1, 5, 12, 25, 40, 60, 80, 110, 150, 190, 230, 300, 300, 300, 300,
]);

const freezeZone = (zone) => Object.freeze({
  ...zone,
  unlock: Object.freeze({ ...zone.unlock }),
  effects: Object.freeze({ ...zone.effects }),
  species: Object.freeze([...zone.species]),
  specialPool: Object.freeze([...(zone.specialPool || [])]),
  specialKind: zone.specialKind || null,
  specialChancePerKill: Number(zone.specialChancePerKill) || 0,
});

/** Pool curada de lendários (Threat 3) — filtrada contra a Pokédex no build do catálogo. */
export const ZONE_LEGENDARY_POOL = Object.freeze([
  'articuno', 'zapdos', 'moltres', 'mewtwo',
  'raikou', 'entei', 'suicune', 'lugia', 'ho-oh',
  'regirock', 'regice', 'registeel',
  'kyogre', 'groudon', 'rayquaza',
  'uxie', 'palkia',
  'heatran', 'regigigas', 'giratina',
]);

/** Pool curada de míticos (Threat 4). */
export const ZONE_MYTHICAL_POOL = Object.freeze([
  'mew', 'celebi', 'jirachi', 'phione', 'darkrai', 'shaymin', 'arceus',
]);

export const ZONE_SPECIAL_CHANCE = Object.freeze({
  legendary: 1 / 40000,
  mythical: 1 / 120000,
});

const GMT3_OFFSET_MS = -3 * 3600000;
const HOUR_MS = 3600000;

/** Índice de hora cheia em GMT-3 (fixo), igual para todos os jogadores. */
export function gmt3HourKey(nowMs = Date.now()) {
  return Math.floor((Number(nowMs) + GMT3_OFFSET_MS) / HOUR_MS);
}

/** Ms até a próxima hora cheia GMT-3. */
export function msUntilNextGmt3Hour(nowMs = Date.now()) {
  const now = Number(nowMs) || 0;
  const key = gmt3HourKey(now);
  const nextMs = (key + 1) * HOUR_MS - GMT3_OFFSET_MS;
  return Math.max(0, nextMs - now);
}

/** Espécie featured da pool da zona na hora GMT-3 atual. */
export function featuredSpecialSpecies(zone, nowMs = Date.now()) {
  const pool = zone?.specialPool;
  if (!pool?.length) return null;
  return pool[((gmt3HourKey(nowMs) % pool.length) + pool.length) % pool.length];
}

/** Roll flat sem pity. */
export function rollZoneSpecial(chance, rng = Math.random) {
  const p = Number(chance) || 0;
  if (p <= 0) return false;
  return rng() < p;
}

/**
 * O endgame usa cinco biomas, em vez de repetir 18 rotas de tipo por faixa.
 * Os niveis 300-500 continuam internos para preservar escala e saves; na UI
 * eles sao apresentados como Apex / Ameaca I-IV.
 * Threat 3/4: pool especial com featured horário (GMT-3); Apex–Threat 2 sem especial.
 */
export const ENDGAME_ZONES = Object.freeze([
  freezeZone({
    id: 'supreme-threshold', band: 10, tier: 0, level: 300, primaryType: 'lutador',
    recommendedPower: 4200,
    unlock: { trainerLevel: 230, mastery: 0, prestige: 0 },
    effects: { enemyHpMult: 1, enemyStatMult: 1, wildDamageMult: 1, xpMult: 1.1, lootMult: 1.1 },
    species: ['charizard', 'venusaur', 'blastoise', 'typhlosion', 'meganium', 'feraligatr', 'sceptile', 'blaziken', 'swampert', 'torterra', 'infernape', 'empoleon', 'garchomp'],
    specialKind: null, specialPool: [], specialChancePerKill: 0,
  }),
  freezeZone({
    id: 'ashen-spire', band: 11, tier: 1, level: 350, primaryType: 'fogo',
    recommendedPower: 4800,
    unlock: { trainerLevel: 300, mastery: 5, prestige: 0 },
    effects: { enemyHpMult: 1.05, enemyStatMult: 1.05, wildDamageMult: 1.12, xpMult: 1.18, lootMult: 1.2 },
    species: ['arcanine', 'magmortar', 'houndoom', 'infernape', 'rhyperior', 'golem', 'camerupt', 'flygon', 'charizard', 'salamence', 'tyranitar', 'torkoal'],
    specialKind: null, specialPool: [], specialChancePerKill: 0,
  }),
  freezeZone({
    id: 'obsidian-court', band: 12, tier: 2, level: 400, primaryType: 'aço',
    recommendedPower: 5200,
    unlock: { trainerLevel: 300, mastery: 15, prestige: 1 },
    effects: { enemyHpMult: 1.18, enemyStatMult: 1.12, wildDamageMult: 1.08, xpMult: 1.28, lootMult: 1.35 },
    species: ['aggron', 'metagross', 'steelix', 'scizor', 'magnezone', 'probopass', 'dusknoir', 'gengar', 'spiritomb', 'weavile', 'tyranitar', 'bastiodon'],
    specialKind: null, specialPool: [], specialChancePerKill: 0,
  }),
  freezeZone({
    id: 'eclipse-rift', band: 13, tier: 3, level: 450, primaryType: 'sombrio',
    recommendedPower: 5500,
    unlock: { trainerLevel: 300, mastery: 30, prestige: 2 },
    effects: { enemyHpMult: 1.25, enemyStatMult: 1.18, wildDamageMult: 1.18, xpMult: 1.4, lootMult: 1.55 },
    species: ['gardevoir', 'gallade', 'gengar', 'dusknoir', 'mismagius', 'spiritomb', 'umbreon', 'espeon', 'honchkrow', 'weavile', 'togekiss', 'alakazam'],
    specialKind: 'legendary',
    specialPool: ZONE_LEGENDARY_POOL,
    specialChancePerKill: ZONE_SPECIAL_CHANCE.legendary,
  }),
  freezeZone({
    id: 'final-horizon', band: 14, tier: 4, level: 500, primaryType: 'dragão',
    recommendedPower: 5800,
    unlock: { trainerLevel: 300, mastery: 50, prestige: 3 },
    effects: { enemyHpMult: 1.4, enemyStatMult: 1.25, wildDamageMult: 1.28, xpMult: 1.6, lootMult: 1.85 },
    species: ['garchomp', 'salamence', 'dragonite', 'metagross', 'tyranitar', 'lucario', 'snorlax', 'slaking', 'togekiss', 'porygon-z', 'magnezone', 'rhyperior'],
    specialKind: 'mythical',
    specialPool: ZONE_MYTHICAL_POOL,
    specialChancePerKill: ZONE_SPECIAL_CHANCE.mythical,
  }),
]);

export function zoneConfigForBand(band) {
  return ENDGAME_ZONES.find((zone) => zone.band === Number(band)) || null;
}

export function endgameZoneAccess(band, progress = {}) {
  const zone = zoneConfigForBand(band);
  if (!zone) return Object.freeze({ unlocked: true, missing: Object.freeze([]), zone: null });
  const values = {
    trainerLevel: Math.max(1, Number(progress.trainerLevel) || 1),
    mastery: Math.max(0, Number(progress.mastery) || 0),
    prestige: Math.max(0, Number(progress.prestige) || 0),
  };
  const missing = Object.entries(zone.unlock)
    .filter(([key, required]) => values[key] < required)
    .map(([key, required]) => Object.freeze({ key, required, current: values[key] }));
  return Object.freeze({ unlocked: missing.length === 0, missing: Object.freeze(missing), zone });
}

export const ZONE_EXCLUSIVE_SPECIES = Object.freeze([
  'garchomp', 'magmortar', 'metagross', 'spiritomb', 'slaking',
]);

const NORMAL_HUNT_BAND_COUNT = 10;

/** Nível de treinador/rota a partir do qual o tipo fica disponível. */
export const TYPE_UNLOCK_LEVEL = Object.freeze({
  normal: 1,
  fogo: 1,
  água: 1,
  planta: 1,
  elétrico: 1,
  inseto: 1,
  voador: 1,
  veneno: 1,
  terra: 1,
  lutador: 1,
  pedra: 1,
  psíquico: 1,
  fantasma: 1,
  sombrio: 1,
  fada: 1,
  gelo: 1,
  aço: 1,
  // Dragões começam raros no Nv.60 e ganham variedade por faixa.
  dragão: 60,
});

/**
 * Piso de rota para espécies hypadas/pseudos.
 * Descendentes herdam o máximo entre o próprio floor e o dos ancestrais.
 */
export const SPECIES_MIN_ROUTE_LEVEL = Object.freeze({
  riolu: 60,
  lucario: 150,

  larvitar: 80,
  pupitar: 100,
  tyranitar: 200,

  beldum: 80,
  metang: 100,
  metagross: 200,

  dratini: 60,
  dragonair: 100,
  dragonite: 150,

  bagon: 80,
  shelgon: 100,
  salamence: 150,

  gible: 80,
  gabite: 100,
  garchomp: 150,
});

export const PROGRESSION_TARGETS = Object.freeze({
  // Ritmo medido pelo sim de combate real (~405 na rota 10, ~1200 na 100, ~1650 na 300).
  // O audit usa este valor flat; o sim (aceitação) usa TTK por faixa.
  expectedDefeatsPerHour: 1200,
  starterLevel1To10MaximumHours: 0.6,
  pokemonLevel100MinimumHours: 12,
  pokemonLevel100MaximumHours: 15,
  // Janelas do audit (ritmo flat). O sim de combate real chega ao Nv.300 em ~60–80 h.
  pokemonLevel300MinimumHours: 80,
  pokemonLevel300MaximumHours: 90,
  trainerLevel200MinimumHours: 44,
  trainerLevel200MaximumHours: 50,
  trainerLevel300MinimumHours: 85,
  trainerLevel300MaximumHours: 96,
});

const RARITY_WEIGHT = Object.freeze({
  Comum: 100,
  Incomum: 58,
  Raro: 24,
  Épico: 9,
  Lendário: 2.6,
  Mítico: 1.3,
});

const RARITY_SCORE = Object.freeze({
  Comum: 0,
  Incomum: 8,
  Raro: 18,
  Épico: 32,
  Lendário: 75,
  Mítico: 90,
});

const MIN_SPECIES_PER_ROUTE = 3;
const MAX_SPECIES_PER_ROUTE = 4;
const BAND_POWER_CENTERS = [
  290, 340, 400, 470, 530, 580, 620, 660, 700, 740, 800,
  840, 880, 920, 960,
];

/** Teto do pool Atlas / zona por faixa (early enxuto; 300+ = variedade da zona). */
export function huntAtlasMaxSpecies(band) {
  const level = routeLevelAt(band);
  if (level <= 60) return 3;
  if (level <= 100) return 4;
  if (level <= 250) return 5;
  if (level <= 350) return 6;
  if (level <= 450) return 7;
  return 8;
}

export function isZoneHuntLevel(level) {
  return (Number(level) || 0) >= ZONE_HUNT_MIN_LEVEL;
}

export function isZoneHuntBand(band) {
  return isZoneHuntLevel(routeLevelAt(band));
}

// Curadoria do Atlas: quando muitas espécies servem para a mesma faixa,
// ícones mais reconhecíveis da franquia ganham prioridade. A lista não ignora
// os filtros de nível/evolução; ela apenas desempata candidatos já coerentes.
const HUNT_FAVORITE_SLUGS = Object.freeze([
  'pikachu', 'charizard', 'eevee', 'lucario', 'gengar', 'dragonite',
  'snorlax', 'gyarados', 'arcanine', 'blastoise', 'venusaur', 'tyranitar',
  'garchomp', 'metagross', 'salamence', 'gardevoir', 'scizor', 'umbreon',
  'espeon', 'jolteon', 'vaporeon', 'flareon', 'luxray', 'infernape',
  'empoleon', 'torterra', 'blaziken', 'sceptile', 'swampert', 'feraligatr',
  'typhlosion', 'meganium', 'alakazam', 'machamp', 'lapras', 'ninetales',
  'absol', 'flygon', 'milotic', 'electivire', 'magmortar', 'togekiss',
  'weavile', 'gallade', 'crobat', 'steelix', 'heracross', 'houndoom',
  'staraptor', 'roserade', 'mamoswine', 'dusknoir', 'porygon-z', 'rhyperior',
  'raichu', 'ampharos', 'charmander', 'squirtle', 'bulbasaur', 'riolu',
  'gastly', 'dratini', 'larvitar', 'gible', 'beldum', 'bagon', 'ralts',
  'growlithe', 'vulpix', 'magikarp', 'psyduck', 'meowth', 'jigglypuff',
  'cubone', 'abra', 'scyther', 'chimchar', 'piplup', 'turtwig', 'torchic',
  'treecko', 'mudkip', 'totodile', 'cyndaquil', 'chikorita', 'shinx',
  'elekid', 'magby', 'togepi', 'sneasel',
]);
const HUNT_FAVORITE_SCORE = new Map(HUNT_FAVORITE_SLUGS.map((slug, index) => (
  [slug, HUNT_FAVORITE_SLUGS.length - index]
)));

// Progressão autoral inspirada na descoberta gradual por áreas do PokéIdle,
// adaptada às faixas fixas do IdleMon. Não replica rotas nem tabelas externas.
const CURATED_HUNT_BANDS = Object.freeze({
  fogo: Object.freeze({
    1: Object.freeze(['charmander']),
    60: Object.freeze(['charmeleon']),
    100: Object.freeze(['charizard']),
  }),
  planta: Object.freeze({
    1: Object.freeze(['bulbasaur']),
    60: Object.freeze(['ivysaur']),
    100: Object.freeze(['venusaur']),
  }),
  água: Object.freeze({
    1: Object.freeze(['squirtle']),
    60: Object.freeze(['wartortle']),
    100: Object.freeze(['blastoise']),
  }),
  dragão: Object.freeze({
    60: Object.freeze(['dratini']),
    80: Object.freeze(['bagon', 'gible']),
    100: Object.freeze(['dragonair', 'shelgon', 'gabite']),
    150: Object.freeze(['altaria', 'flygon']),
    200: Object.freeze(['dragonite']),
    250: Object.freeze(['salamence']),
    300: Object.freeze(['garchomp']),
  }),
});

let registry = null;
let huntCatalog = Object.freeze([]);

const normalizeSlug = (value) => String(value || '').trim().toLowerCase().replace(/_/g, '-');
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export function routeLevelAt(band = 0) {
  return ROUTE_LEVEL_RANGES[clamp(Number(band) || 0, 0, ROUTE_LEVEL_RANGES.length - 1)][0];
}

export function typeUnlockLevel(type) {
  return TYPE_UNLOCK_LEVEL[type] ?? 1;
}

export function isTypeUnlockedForRoute(type, routeLevel) {
  return Number(routeLevel) >= typeUnlockLevel(type);
}

export function isTypeUnlockedForTrainer(type, trainerLevel) {
  return Number(trainerLevel) >= typeUnlockLevel(type);
}

function evolutionDepth(entry, bySlug, stack = new Set()) {
  if (!entry?.evolvesFrom || stack.has(entry.slug)) return 0;
  stack.add(entry.slug);
  return 1 + evolutionDepth(bySlug.get(normalizeSlug(entry.evolvesFrom)), bySlug, stack);
}

function lineageRoot(entry, bySlug) {
  let cursor = entry;
  const seen = new Set();
  while (cursor?.evolvesFrom) {
    if (seen.has(cursor.slug)) break;
    seen.add(cursor.slug);
    const parent = bySlug.get(normalizeSlug(cursor.evolvesFrom));
    if (!parent) break;
    cursor = parent;
  }
  return cursor?.slug || entry.slug;
}

function explicitSpeciesFloor(slug) {
  return SPECIES_MIN_ROUTE_LEVEL[normalizeSlug(slug)] || 1;
}

function naturalPowerFloor(entry) {
  const bst = Number(entry?.bst) || 0;
  if (bst <= 330) return 1;
  if (bst <= 380) return 10;
  if (bst <= 430) return 20;
  if (bst <= 480) return 40;
  if (bst <= 520) return 60;
  if (bst <= 560) return 80;
  if (bst <= 600) return 100;
  return 150;
}

/** Floor efetivo: max(próprio, ancestrais na Pokédex, piso de lendário). */
function speciesMinLevelFor(entry, bySlug, cache) {
  const key = entry.slug;
  if (cache.has(key)) return cache.get(key);
  const parent = entry?.evolvesFrom ? bySlug.get(normalizeSlug(entry.evolvesFrom)) : null;
  const hasAuthoredEvolution = !!authoredEvolutionLevel(parent, key);
  let floor = explicitSpeciesFloor(key);
  if (!entry?.evolvesFrom || !hasAuthoredEvolution) floor = Math.max(floor, naturalPowerFloor(entry));
  if (entry.rarity === 'Mítico') floor = Math.max(floor, 300);
  else if (entry.legendary || entry.rarity === 'Lendário') floor = Math.max(floor, 250);
  let cursor = entry;
  const seen = new Set();
  while (cursor?.evolvesFrom) {
    if (seen.has(cursor.slug)) break;
    seen.add(cursor.slug);
    const parent = bySlug.get(normalizeSlug(cursor.evolvesFrom));
    if (!parent) break;
    floor = Math.max(floor, explicitSpeciesFloor(parent.slug));
    cursor = parent;
  }
  cache.set(key, floor);
  return floor;
}

function speciesPowerScore(entry, depth) {
  return Number(entry.bst || 300) + depth * 25 + (RARITY_SCORE[entry.rarity] || 0);
}

function homeBandFor(entry, depth, minLevel) {
  if (entry.rarity === 'Mítico') return 10;
  if (entry.legendary || entry.rarity === 'Lendário') return 9;

  // Pseudos/hypados: home band nunca abaixo do floor de rota.
  const floorBand = bandForLevel(minLevel);
  let band;
  const score = speciesPowerScore(entry, depth);
  if (score <= 320) band = 0;
  else if (score <= 360) band = 1;
  else if (score <= 410) band = 2;
  else if (score <= 470) band = 3;
  else if (score <= 520) band = 4;
  else if (score <= 555) band = 5;
  else if (score <= 585) band = 6;
  else if (score <= 615) band = 7;
  else if (score <= 645) band = 8;
  else band = 8;

  return Math.max(band, floorBand);
}

/** Estágio preferido por nível de rota (early = só bases). */
function preferredDepthRange(routeLevel) {
  if (routeLevel <= 20) return [0, 0];
  if (routeLevel <= 40) return [0, 1];
  if (routeLevel <= 60) return [0, 1];
  if (routeLevel <= 80) return [0, 2];
  if (routeLevel <= 150) return [1, 2];
  return [1, 2];
}

function stageFit(depth, routeLevel, entry) {
  if (entry.legendary || entry.rarity === 'Lendário' || entry.rarity === 'Mítico') {
    return routeLevel >= 250 ? 1 : routeLevel >= 200 ? 0.35 : 0.05;
  }
  const [minD, maxD] = preferredDepthRange(routeLevel);
  if (depth >= minD && depth <= maxD) return 1;
  if (depth < minD) return clamp(1 - (minD - depth) * 0.55, 0.08, 0.7);
  return clamp(1 - (depth - maxD) * 0.45, 0.12, 0.75);
}

function rawEncounterWeight(entry, depth, homeBand, routeBand, routeLevel) {
  const rarity = RARITY_WEIGHT[entry.rarity] || 18;
  const stagePenalty = [1, 0.82, 0.64, 0.52][Math.min(depth, 3)];
  const distance = Math.abs(routeBand - homeBand);
  const distancePenalty = distance === 0 ? 1 : 0.28 / distance;
  const center = BAND_POWER_CENTERS[routeBand] ?? BAND_POWER_CENTERS.at(-1);
  const powerFit = clamp(1 - Math.abs(speciesPowerScore(entry, depth) - center) / 560, 0.42, 1);
  const stage = stageFit(depth, routeLevel, entry);
  return rarity * stagePenalty * distancePenalty * powerFit * stage;
}

function isChanceCapped(row) {
  return row.rarity === 'Mítico' || row.rarity === 'Lendário' || row.legendary;
}

function normalizeEncounterChances(rows) {
  if (!rows.length) return [];
  const total = rows.reduce((sum, row) => sum + row.weight, 0) || 1;
  const initial = rows.map((row) => ({ ...row, rawChance: row.weight / total * 100 }));
  const capped = initial.map((row) => {
    const cap = row.rarity === 'Mítico' ? 0.75 : isChanceCapped(row) ? 1.5 : null;
    return { ...row, fixedChance: cap == null ? null : Math.min(row.rawChance, cap) };
  });
  const fixedTotal = capped.reduce((sum, row) => sum + (row.fixedChance ?? 0), 0);
  const flexible = capped.filter((row) => row.fixedChance == null);
  const flexibleWeight = flexible.reduce((sum, row) => sum + row.weight, 0);
  const pool = Math.max(0, 100 - fixedTotal);
  const rounded = capped.map(({ rawChance, fixedChance, ...row }) => {
    let chance = fixedChance;
    if (chance == null) {
      chance = flexibleWeight
        ? row.weight / flexibleWeight * pool
        : pool / Math.max(1, capped.length);
    }
    return { ...row, chance: Math.round(chance * 100) / 100 };
  });
  const drift = Math.round((100 - rounded.reduce((sum, row) => sum + row.chance, 0)) * 100) / 100;
  const driftTarget = rounded.find((row) => !isChanceCapped(row)) || rounded[0];
  if (driftTarget) driftTarget.chance = Math.round((driftTarget.chance + drift) * 100) / 100;
  return rounded;
}

function rankEligibleSpecies(typed, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug, band) {
  const routeLevel = routeLevelAt(band);
  return typed
    .filter((entry) => routeLevel >= (minLevelBySlug.get(entry.slug) || 1))
    .map((entry) => {
      const depth = depthBySlug.get(entry.slug) || 0;
      const homeBand = homeBandBySlug.get(entry.slug) || 0;
      return {
        entry,
        depth,
        homeBand,
        root: lineageRoot(entry, bySlug),
        weight: rawEncounterWeight(entry, depth, homeBand, band, routeLevel),
        distance: Math.abs(homeBand - band),
        stageScore: stageFit(depth, routeLevel, entry),
      };
    })
    .sort((a, b) => {
      if (b.stageScore !== a.stageScore) return b.stageScore - a.stageScore;
      if (a.distance !== b.distance) return a.distance - b.distance;
      return b.weight - a.weight || a.entry.slug.localeCompare(b.entry.slug);
    });
}

function pickRouteSpecies(ranked, preferHomeBand = null) {
  const selected = [];
  const usedRoots = new Set();

  const tryAdd = (row, requireUniqueRoot) => {
    if (selected.includes(row.entry)) return false;
    if (requireUniqueRoot && usedRoots.has(row.root) && selected.length >= MIN_SPECIES_PER_ROUTE) return false;
    selected.push(row.entry);
    usedRoots.add(row.root);
    return true;
  };

  const flexibleSlots = Math.max(2, MAX_SPECIES_PER_ROUTE - 1);
  for (const row of ranked) {
    if (selected.filter((entry) => !isChanceCapped(entry)).length >= flexibleSlots) break;
    if (isChanceCapped(row.entry)) continue;
    if (row.stageScore < 0.45) continue;
    tryAdd(row, true);
  }

  if (preferHomeBand != null) {
    const nativeCount = ranked.filter((row) => row.homeBand === preferHomeBand).length;
    const nativeLimit = nativeCount > MAX_SPECIES_PER_ROUTE ? MAX_SPECIES_PER_ROUTE - 1 : MAX_SPECIES_PER_ROUTE;
    for (const row of ranked) {
      if (selected.length >= nativeLimit) break;
      if (row.homeBand !== preferHomeBand) continue;
      tryAdd(row, true);
    }
  }

  for (const row of ranked) {
    if (selected.length >= MIN_SPECIES_PER_ROUTE) break;
    tryAdd(row, true);
  }

  for (const row of ranked) {
    if (selected.length >= MIN_SPECIES_PER_ROUTE) break;
    tryAdd(row, false);
  }

  for (const row of ranked) {
    if (selected.length >= MAX_SPECIES_PER_ROUTE) break;
    tryAdd(row, true);
  }

  return selected;
}

function rowsFromSpecies(selected, bySlug, depthBySlug, homeBandBySlug, band) {
  const routeLevel = routeLevelAt(band);
  const weighted = selected.map((entry) => {
    const depth = depthBySlug.get(entry.slug) || 0;
    const homeBand = homeBandBySlug.get(entry.slug) || 0;
    return {
      slug: entry.slug,
      name: entry.name || entry.slug,
      types: [...entry.types],
      rarity: entry.rarity,
      legendary: !!entry.legendary,
      bst: entry.bst,
      depth,
      homeBand,
      weight: rawEncounterWeight(entry, depth, homeBand, band, routeLevel),
    };
  }).sort((a, b) => b.weight - a.weight || a.slug.localeCompare(b.slug));
  return normalizeEncounterChances(weighted);
}

function recountAppearances(tables) {
  const appearanceCount = new Map();
  for (const rows of tables.values()) {
    for (const row of rows) appearanceCount.set(row.slug, (appearanceCount.get(row.slug) || 0) + 1);
  }
  return appearanceCount;
}

function membersOfLineage(root, species, bySlug) {
  return species.filter((entry) => lineageRoot(entry, bySlug) === root);
}

function authoredEvolutionLevel(parent, childSlug) {
  const link = (parent?.evolvesTo || []).find((candidate) => normalizeSlug(candidate?.slug) === normalizeSlug(childSlug));
  const level = Number(link?.level);
  return Number.isFinite(level) && level > 1 ? Math.floor(level) : null;
}

/** Nível narrativo mínimo da forma, usando o requisito real quando existe. */
function evolutionUnlockLevel(entry, bySlug) {
  if (!entry?.evolvesFrom) return 1;
  const parent = bySlug.get(normalizeSlug(entry.evolvesFrom));
  const authored = authoredEvolutionLevel(parent, entry.slug);
  if (authored) return authored;
  const depth = evolutionDepth(entry, bySlug);
  return depth <= 1 ? 20 : depth === 2 ? 40 : 60 + (depth - 3) * 20;
}

/** A forma deixa de aparecer quando uma evolução seguinte já cabe na faixa. */
function nextEvolutionLevel(entry, bySlug) {
  const levels = (entry?.evolvesTo || []).map((link) => {
    const authored = Number(link?.level);
    if (Number.isFinite(authored) && authored > 1) return Math.floor(authored);
    // Evoluções por pedra, troca ou amizade não aposentam automaticamente a
    // forma anterior em uma faixa fixa: ambas continuam opções válidas.
    return null;
  }).filter(Number.isFinite);
  return levels.length ? Math.min(...levels) : Infinity;
}

function evolutionStageFitsRoute(entry, routeLevel, bySlug) {
  if (routeLevel >= 100 && !isFinalEvolution(entry)) return false;
  return routeLevel >= evolutionUnlockLevel(entry, bySlug)
    && routeLevel < nextEvolutionLevel(entry, bySlug);
}

function isFinalEvolution(entry) {
  return !Array.isArray(entry?.evolvesTo) || entry.evolvesTo.length === 0;
}

function isRestrictedHuntSpecies(entry) {
  return !!entry?.legendary || entry?.rarity === 'Lendário' || entry?.rarity === 'Mítico';
}

function curatedBandForSpecies(slug) {
  for (const bands of Object.values(CURATED_HUNT_BANDS)) {
    for (const [level, slugs] of Object.entries(bands)) {
      if (slugs.includes(slug)) return bandForLevel(Number(level));
    }
  }
  return null;
}

function isCuratedAtlasEntry(type, level, slug) {
  return CURATED_HUNT_BANDS[type]?.[level]?.includes(slug) || false;
}

function preferredAtlasBandFor(entry, depth, homeBand, minLevel) {
  const curatedBand = curatedBandForSpecies(entry.slug);
  if (curatedBand != null) return curatedBand;
  const minBand = bandForLevel(minLevel);
  const finalEvolution = isFinalEvolution(entry);
  let preferredBand = homeBand;

  if (entry.evolvesFrom && !finalEvolution) {
    preferredBand = bandForLevel(60);
  } else if (finalEvolution) {
    const bst = Number(entry.bst) || 0;
    if (bst >= 600) preferredBand = bandForLevel(500);
    else if (bst >= 580) preferredBand = bandForLevel(450);
    else if (bst >= 560) preferredBand = bandForLevel(400);
    else if (bst >= 540) preferredBand = bandForLevel(350);
    else if (bst >= 520) preferredBand = bandForLevel(300);
    else if (bst >= 500) preferredBand = bandForLevel(250);
    else if (bst >= 480) preferredBand = bandForLevel(200);
    else if (depth >= 2) preferredBand = bandForLevel(150);
    else if (entry.evolvesFrom) preferredBand = bandForLevel(100);
  }

  return Math.max(minBand, preferredBand);
}

function atlasCoverageCandidateFits(entry, band, minLevel, bySlug) {
  const level = routeLevelAt(band);
  if (level < minLevel) return false;
  if (level <= 20 && entry.evolvesFrom) return false;
  if (level > 100 && !isFinalEvolution(entry)) return false;
  return level >= evolutionUnlockLevel(entry, bySlug);
}

/**
 * Redistribui apenas entradas automáticas para que os tipos abundantes tenham
 * ao menos uma espécie em cada âncora. A faixa continua sendo propriedade da
 * espécie, portanto Pokémon de tipo duplo nunca aparecem em níveis diferentes.
 */
function fillFullHuntProgression({
  landSpecies, bandBySlug, depthBySlug, minLevelBySlug, bySlug,
}) {
  const fullTypes = new Set(FULL_HUNT_PROGRESSION_TYPES);
  const countAt = (type, band) => landSpecies.reduce((count, entry) => (
    count
    + (bandBySlug.get(entry.slug) === band
      && entry.types?.includes(type)
      && isTypeUnlockedForRoute(type, routeLevelAt(band)) ? 1 : 0)
  ), 0);
  const canLeaveCurrentBand = (entry) => {
    const currentBand = bandBySlug.get(entry.slug);
    return (entry.types || []).every((type) => (
      !fullTypes.has(type)
      || !isTypeUnlockedForRoute(type, routeLevelAt(currentBand))
      || countAt(type, currentBand) > 1
    ));
  };
  const scoreForTarget = (entry, band) => {
    const level = routeLevelAt(band);
    const depth = depthBySlug.get(entry.slug) || 0;
    const power = speciesPowerScore(entry, depth);
    const fame = HUNT_FAVORITE_SCORE.get(entry.slug) || 0;
    const singleTypeBonus = entry.types?.length === 1 ? 2_000 : 0;
    const distancePenalty = Math.abs((bandBySlug.get(entry.slug) || 0) - band) * 180;
    if (level >= 200) return power * 20 + fame - distancePenalty;
    const powerFit = 1_500 - Math.abs(power - BAND_POWER_CENTERS[band]) * 4;
    return singleTypeBonus + powerFit + fame * 3 - distancePenalty;
  };

  // Zonas/bosses do topo primeiro (500→200), depois early mid.
  const targetBands = [0, 1, 2, 3, 4, 5, 6, 7, 9, 8];
  for (const type of FULL_HUNT_PROGRESSION_TYPES) {
    for (const band of targetBands) {
      if (countAt(type, band) > 0) continue;
      const candidate = landSpecies
        .filter((entry) => (
          entry.types?.includes(type)
          && curatedBandForSpecies(entry.slug) == null
          && bandBySlug.get(entry.slug) !== band
          && atlasCoverageCandidateFits(entry, band, minLevelBySlug.get(entry.slug) || 1, bySlug)
          && canLeaveCurrentBand(entry)
        ))
        .sort((a, b) => scoreForTarget(b, band) - scoreForTarget(a, band)
          || a.slug.localeCompare(b.slug))[0];
      if (!candidate) {
        throw new Error(`Sem espécie elegível para cobrir ${type} no Nv.${routeLevelAt(band)}.`);
      }
      bandBySlug.set(candidate.slug, band);
    }
  }
}

/** Distribui cada especie terrestre por uma unica faixa normal equilibrada. */
function assignNormalHuntBands(landSpecies, depthBySlug, homeBandBySlug, minLevelBySlug, bySlug) {
  const total = landSpecies.length;
  const base = Math.floor(total / NORMAL_HUNT_BAND_COUNT);
  const remainder = total % NORMAL_HUNT_BAND_COUNT;
  const targets = Array.from({ length: NORMAL_HUNT_BAND_COUNT }, (_, band) => (
    base + (band >= NORMAL_HUNT_BAND_COUNT - remainder ? 1 : 0)
  ));
  const counts = Array(NORMAL_HUNT_BAND_COUNT).fill(0);
  const placements = landSpecies.map((entry) => {
    const depth = depthBySlug.get(entry.slug) || 0;
    const homeBand = homeBandBySlug.get(entry.slug) || 0;
    const minLevel = minLevelBySlug.get(entry.slug) || 1;
    const preferred = Math.min(NORMAL_HUNT_BAND_COUNT - 1,
      preferredAtlasBandFor(entry, depth, homeBand, minLevel));
    const eligible = Array.from({ length: NORMAL_HUNT_BAND_COUNT }, (_, band) => band)
      .filter((band) => atlasCoverageCandidateFits(entry, band, minLevel, bySlug));
    if (!eligible.length) throw new Error(`Sem faixa normal elegivel para ${entry.slug}.`);
    const curated = curatedBandForSpecies(entry.slug);
    return { entry, preferred, eligible, curated: eligible.includes(curated) ? curated : null };
  }).sort((a, b) => (
    Number(b.curated != null) - Number(a.curated != null)
    || a.eligible.length - b.eligible.length
    || a.entry.slug.localeCompare(b.entry.slug)
  ));

  const result = new Map();
  for (const placement of placements) {
    let chosen = placement.curated;
    if (chosen == null) {
      chosen = [...placement.eligible].sort((a, b) => {
        const aOverflow = Math.max(0, counts[a] + 1 - targets[a]);
        const bOverflow = Math.max(0, counts[b] + 1 - targets[b]);
        return aOverflow - bOverflow
          || Math.abs(a - placement.preferred) - Math.abs(b - placement.preferred)
          || counts[a] / Math.max(1, targets[a]) - counts[b] / Math.max(1, targets[b])
          || a - b;
      })[0];
    }
    result.set(placement.entry.slug, chosen);
    counts[chosen] += 1;
  }
  return result;
}

function smoothNormalBandPower(landSpecies, bandBySlug, depthBySlug, minLevelBySlug, bySlug) {
  const fullTypes = new Set(FULL_HUNT_PROGRESSION_TYPES);
  const countAt = (type, band) => landSpecies.reduce((count, entry) => count + Number(
    bandBySlug.get(entry.slug) === band && entry.types?.includes(type),
  ), 0);
  const canLeave = (entry, band, replacement) => (entry.types || []).every((type) => (
    !fullTypes.has(type) || replacement.types?.includes(type) || countAt(type, band) > 1
  ));
  const power = (entry) => speciesPowerScore(entry, depthBySlug.get(entry.slug) || 0);

  for (let lowerBand = 0; lowerBand < NORMAL_HUNT_BAND_COUNT - 1; lowerBand++) {
    const upperBand = lowerBand + 1;
    for (let pass = 0; pass < 80; pass++) {
      const lower = landSpecies.filter((entry) => bandBySlug.get(entry.slug) === lowerBand
        && curatedBandForSpecies(entry.slug) == null);
      const upper = landSpecies.filter((entry) => bandBySlug.get(entry.slug) === upperBand
        && curatedBandForSpecies(entry.slug) == null);
      const pair = lower.flatMap((a) => upper.map((b) => ({ a, b, gain: power(a) - power(b) })))
        .filter(({ a, b, gain }) => gain > 0
          && atlasCoverageCandidateFits(a, upperBand, minLevelBySlug.get(a.slug) || 1, bySlug)
          && atlasCoverageCandidateFits(b, lowerBand, minLevelBySlug.get(b.slug) || 1, bySlug)
          && canLeave(a, lowerBand, b) && canLeave(b, upperBand, a))
        .sort((a, b) => b.gain - a.gain || a.a.slug.localeCompare(b.a.slug))[0];
      if (!pair) break;
      bandBySlug.set(pair.a.slug, upperBand);
      bandBySlug.set(pair.b.slug, lowerBand);
    }
  }
}

function selectAtlasCandidates(candidates, { type, band, depthBySlug, homeBandBySlug }) {
  const level = routeLevelAt(band);
  const boss = level >= 200;
  return candidates.map((entry) => {
    const depth = depthBySlug.get(entry.slug) || 0;
    const homeBand = homeBandBySlug.get(entry.slug) || 0;
    return {
      entry,
      curated: isCuratedAtlasEntry(type, level, entry.slug),
      fame: HUNT_FAVORITE_SCORE.get(entry.slug) || 0,
      power: speciesPowerScore(entry, depth),
      weight: rawEncounterWeight(entry, depth, homeBand, band, level),
    };
  }).sort((a, b) => {
    if (a.curated !== b.curated) return a.curated ? -1 : 1;
    // Bosses precisam ser fortes antes de populares. Nas hunts comuns, fama
    // decide primeiro entre as espécies que já passaram pelos filtros da faixa.
    if (boss && b.power !== a.power) return b.power - a.power;
    if (b.fame !== a.fame) return b.fame - a.fame;
    if (!boss && b.weight !== a.weight) return b.weight - a.weight;
    return b.power - a.power || a.entry.slug.localeCompare(b.entry.slug);
  }).slice(0, huntAtlasMaxSpecies(band)).map((candidate) => candidate.entry);
}

function eligibleBandsForEntry(entry, minLevelBySlug, bySlug) {
  const minLevel = minLevelBySlug.get(entry.slug) || 1;
  const bands = [];
  for (let band = 0; band < ROUTE_LEVEL_RANGES.length; band++) {
    const routeLevel = routeLevelAt(band);
    if (routeLevel < minLevel) continue;
    for (const type of entry.types || []) {
      if (!isTypeUnlockedForRoute(type, routeLevel)) continue;
      bands.push({ type, band, distance: Math.abs(band - bandForLevel(minLevel)) });
    }
  }
  return bands.sort((a, b) => a.distance - b.distance || a.band - b.band || a.type.localeCompare(b.type));
}

/**
 * Inverte as tabelas tipo×faixa: cada linha passa a representar uma hunt de
 * uma única espécie. Espécies aquatic_only continuam exclusivas da pesca.
 */
export function buildSpeciesHuntCatalog(pokedex) {
  const species = Array.isArray(pokedex?.species) ? pokedex.species : Array.isArray(pokedex) ? pokedex : [];
  if (!species.length) throw new Error('Pokédex vazio: não é possível montar o catálogo de hunts.');
  const bySlug = new Map(species.map((entry) => [normalizeSlug(entry.slug), entry]));
  const depthBySlug = new Map(species.map((entry) => [entry.slug, evolutionDepth(entry, bySlug)]));
  const minLevelCache = new Map();
  const minLevelBySlug = new Map(species.map((entry) => [entry.slug, speciesMinLevelFor(entry, bySlug, minLevelCache)]));
  const homeBandBySlug = new Map(species.map((entry) => [
    entry.slug,
    homeBandFor(entry, depthBySlug.get(entry.slug) || 0, minLevelBySlug.get(entry.slug) || 1),
  ]));
  const rows = [];
  const allLandSpecies = species.filter((entry) => (
    entry?.slug
    && !isAquaticOnly(entry.slug)
    && !isRestrictedHuntSpecies(entry)
  ));
  const zoneExclusive = new Set(ZONE_EXCLUSIVE_SPECIES);
  const landSpecies = allLandSpecies.filter((entry) => !zoneExclusive.has(entry.slug));

  const bandBySlug = assignNormalHuntBands(
    landSpecies, depthBySlug, homeBandBySlug, minLevelBySlug, bySlug,
  );
  fillFullHuntProgression({ landSpecies, bandBySlug, depthBySlug, minLevelBySlug, bySlug });
  smoothNormalBandPower(landSpecies, bandBySlug, depthBySlug, minLevelBySlug, bySlug);

  const candidatesByRoute = new Map();
  for (const entry of landSpecies) {
    const band = bandBySlug.get(entry.slug);
    const level = routeLevelAt(band);
    for (const type of entry.types || []) {
      if (!ROUTE_TYPES.includes(type) || !isTypeUnlockedForRoute(type, level)) continue;
      const key = `${type}:${band}`;
      if (!candidatesByRoute.has(key)) candidatesByRoute.set(key, []);
      candidatesByRoute.get(key).push(entry);
    }
  }

  for (let band = 0; band < NORMAL_HUNT_BAND_COUNT; band++) {
    const level = routeLevelAt(band);
    for (const type of ROUTE_TYPES) {
      if (!isTypeUnlockedForRoute(type, level)) continue;
      const candidates = selectAtlasCandidates(candidatesByRoute.get(`${type}:${band}`) || [], {
        type, band, depthBySlug, homeBandBySlug,
      });

      for (const entry of candidates) {
        const depth = depthBySlug.get(entry.slug) || 0;
        const homeBand = homeBandBySlug.get(entry.slug) || 0;
        const weight = rawEncounterWeight(entry, depth, homeBand, band, level);
        rows.push(Object.freeze({
          slug: entry.slug,
          level,
          band,
          type,
          boss: level >= 200,
          zone: false,
          weight: Math.round(weight * 1000000) / 1000000,
        }));
      }
    }
  }

  injectOrphanHuntEntries(rows, {
    landSpecies, bandBySlug, depthBySlug, homeBandBySlug, minLevelBySlug, bySlug,
  });

  for (const zone of ENDGAME_ZONES) {
    for (const slug of zone.species) {
      const entry = bySlug.get(slug);
      if (!entry) throw new Error(`Especie de zona ausente da Pokedex: ${slug}.`);
      rows.push(Object.freeze({
        slug, level: zone.level, band: zone.band,
        type: entry.types?.[0] || zone.primaryType,
        boss: false, legendaryBoss: false, zone: true, zoneId: zone.id, weight: 1,
      }));
    }
    for (const slug of zone.specialPool || []) {
      const entry = bySlug.get(slug);
      if (!entry) throw new Error(`Especial de zona ausente da Pokedex: ${slug}.`);
      rows.push(Object.freeze({
        slug, level: zone.level, band: zone.band,
        type: entry.types?.[0] || zone.primaryType,
        boss: true, legendaryBoss: true, zone: true, zoneId: zone.id,
        specialKind: zone.specialKind || null, weight: 0,
      }));
    }
  }

  rows.sort((a, b) => a.band - b.band || a.type.localeCompare(b.type)
    || b.weight - a.weight || a.slug.localeCompare(b.slug));
  huntCatalog = Object.freeze(rows);
  return huntCatalog;
}

/**
 * Garante ≥1 entrada no Atlas para cada espécie terrestre elegível.
 * Prefere a faixa preferida; se saturada, sobe para faixas mais altas (zonas).
 */
function injectOrphanHuntEntries(rows, {
  landSpecies, bandBySlug, depthBySlug, homeBandBySlug, minLevelBySlug, bySlug,
}) {
  const covered = new Set(rows.map((row) => row.slug));
  const countAt = (type, band) => rows.reduce((n, row) => (
    n + (row.type === type && row.band === band ? 1 : 0)
  ), 0);

  for (const entry of landSpecies) {
    if (covered.has(entry.slug)) continue;
    const preferredBand = bandBySlug.get(entry.slug) ?? 0;
    const minLevel = minLevelBySlug.get(entry.slug) || 1;
    const depth = depthBySlug.get(entry.slug) || 0;
    const homeBand = homeBandBySlug.get(entry.slug) || 0;

    const bandOrder = [];
    for (let band = preferredBand; band < NORMAL_HUNT_BAND_COUNT; band++) bandOrder.push(band);
    for (let band = preferredBand - 1; band >= 0; band--) bandOrder.push(band);

    let placed = false;
    for (const preferUnderCap of [true, false]) {
      for (const band of bandOrder) {
        if (!atlasCoverageCandidateFits(entry, band, minLevel, bySlug)) continue;
        const level = routeLevelAt(band);
        const types = (entry.types || []).filter((type) => (
          ROUTE_TYPES.includes(type) && isTypeUnlockedForRoute(type, level)
        ));
        if (!types.length) continue;
        const type = [...types].sort((a, b) => countAt(a, band) - countAt(b, band)
          || a.localeCompare(b))[0];
        if (preferUnderCap && countAt(type, band) >= huntAtlasMaxSpecies(band)) continue;
        const weight = rawEncounterWeight(entry, depth, homeBand, band, level);
        rows.push(Object.freeze({
          slug: entry.slug,
          level,
          band,
          type,
          boss: level >= 200,
          zone: false,
          weight: Math.round(weight * 1000000) / 1000000,
        }));
        bandBySlug.set(entry.slug, band);
        covered.add(entry.slug);
        placed = true;
        break;
      }
      if (placed) break;
    }
  }
}

/** Catálogo imutável montado pela última chamada a build/configure. */
export function speciesHuntEntries() {
  return huntCatalog;
}

/**
 * Migra save tipo×faixa in-place e também o devolve. O maior peso equivalente
 * vira o foco; um huntSpecies já válido sempre é preservado.
 */
export function migrateLegacyHuntState(state) {
  if (!state || typeof state !== 'object') return state;
  if (!huntCatalog.length) return state;

  const band = clamp(Math.floor(Number(state.range) || 0), 0, ROUTE_LEVEL_RANGES.length - 1);
  const level = routeLevelAt(band);
  if (isZoneHuntLevel(level)) {
    state.huntMode = 'zone';
    state.huntSpecies = null;
    state.huntLevel = level;
    state.range = band;
    return state;
  }

  const existing = normalizeSlug(state.huntSpecies);
  const existingEntry = existing && huntCatalog.find((entry) => entry.slug === existing);
  if (existingEntry && !isZoneHuntLevel(existingEntry.level)) {
    state.huntSpecies = existingEntry.slug;
    state.huntLevel = Number(state.huntLevel) || existingEntry.level;
    state.huntMode = 'species';
    return state;
  }

  const legacyType = typeof state.type === 'string'
    ? state.type
    : ROUTE_TYPES[clamp(Math.floor(Number(state.type) || 0), 0, ROUTE_TYPES.length - 1)];
  const candidates = huntCatalog.filter((entry) => (
    entry.band === band && entry.type === legacyType && !isZoneHuntLevel(entry.level)
  ));
  const fallback = huntCatalog.filter((entry) => entry.band === band && !isZoneHuntLevel(entry.level));
  const anySpecies = huntCatalog.filter((entry) => !isZoneHuntLevel(entry.level));
  const chosen = candidates[0] || fallback[0] || anySpecies[0] || huntCatalog[0];
  state.huntSpecies = chosen.slug;
  state.huntLevel = chosen.level;
  state.huntMode = 'species';
  return state;
}

function ensureHypedBases(tables, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug) {
  for (const slug of Object.keys(SPECIES_MIN_ROUTE_LEVEL)) {
    const entry = bySlug.get(normalizeSlug(slug));
    if (!entry) continue;
    let found = false;
    for (const rows of tables.values()) {
      if (rows.some((row) => row.slug === entry.slug)) { found = true; break; }
    }
    if (found) continue;

    const candidates = eligibleBandsForEntry(entry, minLevelBySlug, bySlug);
    const floor = minLevelBySlug.get(entry.slug) || 1;
    const preferred = candidates.filter((c) => routeLevelAt(c.band) === floor);
    const order = preferred.length ? preferred : candidates;
    for (const { type, band } of order) {
      const key = `${type}:${band}`;
      const current = tables.get(key) || [];
      if (current.length < MAX_SPECIES_PER_ROUTE) {
        tables.set(key, rowsFromSpecies(
          [...current.map((row) => bySlug.get(row.slug)).filter(Boolean), entry],
          bySlug, depthBySlug, homeBandBySlug, band,
        ));
        break;
      }
      const displace = [...current]
        .filter((row) => !SPECIES_MIN_ROUTE_LEVEL[row.slug])
        .sort((a, b) => a.weight - b.weight || a.slug.localeCompare(b.slug))[0];
      if (!displace) continue;
      const kept = current.filter((row) => row.slug !== displace.slug).map((row) => bySlug.get(row.slug)).filter(Boolean);
      tables.set(key, rowsFromSpecies([...kept, entry], bySlug, depthBySlug, homeBandBySlug, band));
      break;
    }
  }
}

function ensureLineageCoverage(tables, species, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug) {
  const roots = [...new Set(species.map((entry) => lineageRoot(entry, bySlug)))];
  for (const root of roots) {
    const members = membersOfLineage(root, species, bySlug);
    const covered = members.some((entry) => {
      for (const rows of tables.values()) {
        if (rows.some((row) => row.slug === entry.slug)) return true;
      }
      return false;
    });
    if (covered) continue;

    const preferred = [bySlug.get(root), ...members.sort((a, b) => (a.bst || 0) - (b.bst || 0))].filter(Boolean);
    let placed = false;
    for (const entry of preferred) {
      const candidates = eligibleBandsForEntry(entry, minLevelBySlug, bySlug);
      for (const { type, band } of candidates) {
        const key = `${type}:${band}`;
        const current = tables.get(key) || [];
        if (current.length < MAX_SPECIES_PER_ROUTE) {
          tables.set(key, rowsFromSpecies(
            [...current.map((row) => bySlug.get(row.slug)).filter(Boolean), entry],
            bySlug, depthBySlug, homeBandBySlug, band,
          ));
          placed = true;
          break;
        }
      }
      if (placed) break;

      const appearanceCount = recountAppearances(tables);
      for (const requireDuplicate of [true, false]) {
        for (const { type, band } of candidates) {
          const key = `${type}:${band}`;
          const current = tables.get(key) || [];
          const displace = [...current]
            .filter((row) => !requireDuplicate || (appearanceCount.get(row.slug) || 0) > 1)
            .sort((a, b) => a.weight - b.weight || a.slug.localeCompare(b.slug))[0];
          if (!displace) continue;
          const kept = current.filter((row) => row.slug !== displace.slug).map((row) => bySlug.get(row.slug)).filter(Boolean);
          tables.set(key, rowsFromSpecies([...kept, entry], bySlug, depthBySlug, homeBandBySlug, band));
          placed = true;
          break;
        }
        if (placed) break;
      }
      if (placed) break;
    }
  }
}

export function configureBalanceData(pokedex) {
  const species = Array.isArray(pokedex?.species) ? pokedex.species : [];
  if (!species.length) throw new Error('Pokédex vazio: não é possível montar o balanceamento.');
  const bySlug = new Map(species.map((entry) => [normalizeSlug(entry.slug), entry]));
  const depthBySlug = new Map(species.map((entry) => [entry.slug, evolutionDepth(entry, bySlug)]));
  const minLevelCache = new Map();
  const minLevelBySlug = new Map(species.map((entry) => [entry.slug, speciesMinLevelFor(entry, bySlug, minLevelCache)]));
  const homeBandBySlug = new Map(species.map((entry) => [
    entry.slug,
    homeBandFor(entry, depthBySlug.get(entry.slug) || 0, minLevelBySlug.get(entry.slug) || 1),
  ]));
  const encounters = new Map();

  for (const type of ROUTE_TYPES) {
    for (let band = 0; band < ROUTE_LEVEL_RANGES.length; band++) {
      const routeLevel = routeLevelAt(band);
      const key = `${type}:${band}`;
      if (!isTypeUnlockedForRoute(type, routeLevel)) {
        encounters.set(key, []);
        continue;
      }
      const typed = species.filter((entry) => entry.types?.includes(type) && !isAquaticOnly(entry.slug));
      const ranked = rankEligibleSpecies(typed, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug, band);
      const selected = pickRouteSpecies(ranked, band);
      encounters.set(key, rowsFromSpecies(selected, bySlug, depthBySlug, homeBandBySlug, band));
    }
  }

  const routeSpecies = species.filter((entry) => !isAquaticOnly(entry.slug));
  ensureLineageCoverage(encounters, routeSpecies, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug);
  ensureHypedBases(encounters, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug);
  // Segurança: nada aquático deve voltar às rotas de hunt.
  for (const [key, rows] of encounters) {
    const filtered = rows.filter((row) => !isAquaticOnly(row.slug));
    if (filtered.length !== rows.length) {
      encounters.set(key, rowsFromSpecies(
        filtered.map((row) => bySlug.get(row.slug)).filter(Boolean),
        bySlug, depthBySlug, homeBandBySlug, Number(key.split(':')[1]) || 0,
      ));
    }
  }

  const fishing = new Map();
  const aquatic = species.filter((entry) => isAquaticOnly(entry.slug));
  const fishingTables = new Map();
  for (let band = 0; band < ROUTE_LEVEL_RANGES.length; band++) {
    const ranked = rankEligibleSpecies(aquatic, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug, band);
    const selected = pickRouteSpecies(ranked, band);
    const rows = rowsFromSpecies(selected, bySlug, depthBySlug, homeBandBySlug, band);
    fishingTables.set(`água:${band}`, rows);
  }
  ensureLineageCoverage(fishingTables, aquatic, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug);
  // Garante que cada aquatic_only aparece em ≥1 banda (pesca admite até 8 spp/banda).
  const FISHING_MAX = 8;
  for (const entry of aquatic) {
    let found = false;
    for (const rows of fishingTables.values()) {
      if (rows.some((row) => row.slug === entry.slug)) { found = true; break; }
    }
    if (found) continue;
    const home = homeBandBySlug.get(entry.slug) || 0;
    const minBand = bandForLevel(minLevelBySlug.get(entry.slug) || 1);
    let targetBand = Math.max(minBand, Math.min(ROUTE_LEVEL_RANGES.length - 1, home));
    let bestKey = `água:${targetBand}`;
    let bestLen = (fishingTables.get(bestKey) || []).length;
    for (let band = minBand; band < ROUTE_LEVEL_RANGES.length; band++) {
      const key = `água:${band}`;
      const len = (fishingTables.get(key) || []).length;
      if (len < bestLen) {
        bestLen = len;
        bestKey = key;
        targetBand = band;
      }
    }
    const current = fishingTables.get(bestKey) || [];
    if (current.length >= FISHING_MAX) continue;
    fishingTables.set(bestKey, rowsFromSpecies(
      [...current.map((row) => bySlug.get(row.slug)).filter(Boolean), entry],
      bySlug, depthBySlug, homeBandBySlug, targetBand,
    ));
  }
  // Segunda passagem: se ainda faltar, força append no home band (acima do soft max).
  for (const entry of aquatic) {
    let found = false;
    for (const rows of fishingTables.values()) {
      if (rows.some((row) => row.slug === entry.slug)) { found = true; break; }
    }
    if (found) continue;
    const home = Math.max(bandForLevel(minLevelBySlug.get(entry.slug) || 1), Math.min(ROUTE_LEVEL_RANGES.length - 1, homeBandBySlug.get(entry.slug) || 0));
    const key = `água:${home}`;
    const current = fishingTables.get(key) || [];
    fishingTables.set(key, rowsFromSpecies(
      [...current.map((row) => bySlug.get(row.slug)).filter(Boolean), entry],
      bySlug, depthBySlug, homeBandBySlug, home,
    ));
  }
  for (let band = 0; band < ROUTE_LEVEL_RANGES.length; band++) {
    fishing.set(band, fishingTables.get(`água:${band}`) || []);
  }
  for (const entry of aquatic) {
    const appears = [...fishing.values()].some((rows) => rows.some((row) => row.slug === entry.slug));
    if (appears) continue;
    const band = Math.max(0, bandForLevel(minLevelBySlug.get(entry.slug) || 1));
    const current = fishing.get(band) || [];
    fishing.set(band, rowsFromSpecies(
      [...current.map((row) => bySlug.get(row.slug)).filter(Boolean), entry],
      bySlug, depthBySlug, homeBandBySlug, band,
    ));
  }

  buildSpeciesHuntCatalog(pokedex);
  registry = { species, bySlug, depthBySlug, homeBandBySlug, minLevelBySlug, encounters, fishing, huntCatalog };
  return registry;
}

export function isBalanceReady() {
  return !!registry;
}

export function speciesEntry(slug) {
  return registry?.bySlug.get(normalizeSlug(slug)) || null;
}

export function speciesEvolutionDepth(slug) {
  return registry?.depthBySlug.get(normalizeSlug(slug)) || 0;
}

export function speciesHomeBand(slug) {
  return registry?.homeBandBySlug.get(normalizeSlug(slug)) ?? 0;
}

export function speciesMinRouteLevel(slug) {
  const key = normalizeSlug(slug);
  if (registry?.minLevelBySlug?.has(key)) return registry.minLevelBySlug.get(key);
  return explicitSpeciesFloor(key);
}

export function bandForLevel(level) {
  const value = Math.max(1, Number(level) || 1);
  let best = 0;
  for (let i = 0; i < ROUTE_LEVEL_RANGES.length; i++) {
    if (ROUTE_LEVEL_RANGES[i][0] <= value) best = i;
    else break;
  }
  return best;
}

export function routeEncounters(type, band) {
  return registry?.encounters.get(`${type}:${clamp(Number(band) || 0, 0, ROUTE_LEVEL_RANGES.length - 1)}`) || [];
}

export function fishingEncounters(band) {
  return registry?.fishing?.get(clamp(Number(band) || 0, 0, ROUTE_LEVEL_RANGES.length - 1)) || [];
}

export function rollFishingEncounter(band, random = Math.random) {
  const rows = fishingEncounters(band);
  if (!rows.length) return null;
  let cursor = clamp(Number(random()) || 0, 0, 0.999999) * 100;
  for (const row of rows) {
    cursor -= row.chance;
    if (cursor <= 0) return row.slug;
  }
  return rows.at(-1).slug;
}

export function rollRouteEncounter(type, band, random = Math.random) {
  const rows = routeEncounters(type, band);
  if (!rows.length) return null;
  let cursor = clamp(Number(random()) || 0, 0, 0.999999) * 100;
  for (const row of rows) {
    cursor -= row.chance;
    if (cursor <= 0) return row.slug;
  }
  return rows.at(-1).slug;
}

export function speciesEncounterLocations(slug) {
  const key = normalizeSlug(slug);
  const locations = [];
  for (const type of ROUTE_TYPES) {
    // The Atlas uses a species-focused catalog, not the legacy random
    // encounter table. Keep Pokédex locations aligned with that catalog.
    const bands = huntCatalog
      .filter((entry) => !entry.zone && entry.type === type && entry.slug === key)
      .map((entry) => ({ band: entry.band, range: ROUTE_LEVEL_RANGES[entry.band] }));
    if (bands.length) locations.push({ type, bands, mode: 'hunt' });
  }
  for (const zone of ENDGAME_ZONES) {
    const bands = huntCatalog
      .filter((entry) => entry.zoneId === zone.id && entry.slug === key)
      .map((entry) => ({ band: entry.band, range: ROUTE_LEVEL_RANGES[entry.band] }));
    if (bands.length) locations.push({
      type: 'zone', zoneId: zone.id, bands, mode: 'zone',
      legendaryBoss: (zone.specialPool || []).includes(key),
      specialKind: zone.specialKind || null,
    });
  }
  const fishBands = [];
  for (let band = 0; band < ROUTE_LEVEL_RANGES.length; band++) {
    const encounter = fishingEncounters(band).find((row) => row.slug === key);
    if (encounter) fishBands.push({ band, range: ROUTE_LEVEL_RANGES[band], chance: encounter.chance });
  }
  if (fishBands.length) locations.push({ type: 'pesca', bands: fishBands, mode: 'fishing' });
  return locations;
}

export function lineageForSpecies(slug) {
  const entry = speciesEntry(slug);
  if (!entry) return null;
  const ancestors = [];
  let cursor = entry;
  const seen = new Set([entry.slug]);
  while (cursor?.evolvesFrom) {
    cursor = speciesEntry(cursor.evolvesFrom);
    if (!cursor || seen.has(cursor.slug)) break;
    seen.add(cursor.slug);
    ancestors.unshift(cursor.slug);
  }
  const descendants = [];
  cursor = entry;
  while (cursor?.evolvesTo?.length) {
    const next = [...cursor.evolvesTo].sort((a, b) => (a.level || 999) - (b.level || 999) || a.id - b.id)[0];
    cursor = speciesEntry(next.slug);
    if (!cursor || seen.has(cursor.slug)) break;
    seen.add(cursor.slug);
    descendants.push(cursor.slug);
  }
  return [...ancestors, entry.slug, ...descendants];
}

export function pokemonXpToNext(level) {
  const value = Math.max(1, Math.floor(Number(level) || 1));
  // Onboarding inspirado na leitura do concorrente contra a mesma criatura Nv.1:
  // 10%, 10%, 5%, 2,5% e 1,25% por KO nos níveis 1–5.
  const earlyCurve = [0, 160, 160, 320, 640, 1280];
  if (value <= 5) return earlyCurve[value];
  // Níveis 6–10 intactos: o onboarding e os checks early permanecem calibrados.
  if (value <= 10) {
    const earlyBridge = Math.max(0, 10 - value) * 25;
    return Math.round(550 + value * 95 + value * value * 2.85 + earlyBridge);
  }
  // A partir do 11: curva alongada para o ritmo medido (~1200 abates/h na rota 100),
  // de modo que o sim de combate real chegue ao Nv.300 em ~60–80 h.
  // Espelha pokemon_xp_to_next no banco.
  return Math.round(550 + value * 180 + value * value * 18);
}

export function trainerXpToNextLevel(level) {
  const value = Math.max(1, Number(level) || 1);
  // Acompanha o total do Pokémon (0,95–1,08×). Espelha trainer_xp_to_next no banco.
  return Math.round(2500 + value * 280 + value * value * 18);
}

/**
 * XP de treinador por abate. Mesma base de combate do Pokémon (nível do inimigo),
 * porque as duas curvas são calibradas juntas. O servidor pagava 12 fixos em
 * qualquer rota — 260x menos que o parceiro na rota 300. Espelha
 * trainer_xp_for_kill no banco: sem dificuldade por BST e sem risco de nível,
 * que são conhecimento do cliente.
 */
export function trainerXpForKill(wildLevel, prestige = 0, defeats = 0) {
  const level = Math.max(1, Math.min(500, Math.floor(Number(wildLevel) || 1)));
  const safePrestige = Math.max(0, Math.min(MAX_TRAINER_PRESTIGE, Math.floor(Number(prestige) || 0)));
  const veteran = killXpBonusForDefeats(defeats);
  const base = level * TRAINER_XP_PER_KILL_LEVEL_FACTOR + TRAINER_XP_PER_KILL_BASE;
  return Math.max(1, Math.round(base * (1 + veteran) * (1 + safePrestige * 0.02)));
}

export const MAX_MASTERY_LEVEL = 50;
export const MAX_TRAINER_PRESTIGE = 10;
export const MAX_TRAINER_TALENT_RANK = 5;
export const TRAINER_TALENT_KEYS = Object.freeze(['trainer', 'hunter', 'catcher', 'researcher']);
export const KILLS_PER_XP_BONUS_STEP = 1000;
export const XP_BONUS_PER_KILL_STEP = 0.001;
export const MAX_KILL_XP_BONUS = 0.2;

/** XP por abate do treinador — espelhado em market_config['progress.server']. */
export const TRAINER_XP_PER_KILL_BASE = 12;
export const TRAINER_XP_PER_KILL_LEVEL_FACTOR = 8;
/**
 * XP offline do Pokémon ativo: nada de graça. Cada rank de Mentor libera 10 pontos
 * percentuais e o VIP soma 25, com teto de 75%. Espelha offline_pokemon_xp_pct.
 */
export const OFFLINE_POKEMON_PCT_PER_RANK = 10;
export const OFFLINE_POKEMON_PCT_VIP = 25;
export const OFFLINE_POKEMON_PCT_MAX = 75;

export function offlinePokemonXpPct(talents = {}, isVip = false) {
  const rank = Math.max(0, Math.min(MAX_TRAINER_TALENT_RANK, Math.floor(Number(talents?.trainer) || 0)));
  const pct = rank * OFFLINE_POKEMON_PCT_PER_RANK + (isVip ? OFFLINE_POKEMON_PCT_VIP : 0);
  return Math.min(OFFLINE_POKEMON_PCT_MAX, pct);
}

export function pokemonMasteryXpToNext(masteryLevel = 0) {
  const level = Math.max(0, Math.min(MAX_MASTERY_LEVEL, Math.floor(Number(masteryLevel) || 0)));
  return Math.round(250000 * (1 + level * 0.32));
}

export function masteryDamageMultiplier(masteryLevel = 0) {
  const level = Math.max(0, Math.min(MAX_MASTERY_LEVEL, Math.floor(Number(masteryLevel) || 0)));
  return 1 + level * 0.004;
}

/** BenefÃ­cios graduais que fazem o nÃ­vel do treinador participar do loop. */
export function normalizeTrainerTalents(talents = {}) {
  return Object.freeze(Object.fromEntries(TRAINER_TALENT_KEYS.map((key) => [
    key,
    Math.max(0, Math.min(MAX_TRAINER_TALENT_RANK, Math.floor(Number(talents?.[key]) || 0))),
  ])));
}

export function trainerTalentPoints(level = 1, prestige = 0) {
  const safeLevel = Math.max(1, Math.min(300, Math.floor(Number(level) || 1)));
  const safePrestige = Math.max(0, Math.min(MAX_TRAINER_PRESTIGE, Math.floor(Number(prestige) || 0)));
  return Math.floor(safeLevel / 50) + safePrestige;
}

/** Pontos investíveis conquistados por marcos de derrotas (1 por 1.000 kills). */
export function trainerTalentPointsFromDefeats(defeats = 0) {
  return Math.max(0, Math.floor((Number(defeats) || 0) / 1000));
}

/**
 * In server-authoritative gameplay, pending local kills must not unlock a
 * talent before the progress row has credited them.
 */
export function authoritativeDefeats({
  synced = false,
  serverDefeated = null,
  localDefeated = 0,
} = {}) {
  const local = Number(localDefeated);
  const safeLocal = Number.isFinite(local) ? Math.max(0, Math.floor(local)) : 0;
  const server = Number(serverDefeated);
  if (!synced || !Number.isFinite(server)) return safeLocal;
  return Math.max(0, Math.floor(server));
}

export function trainerTalentPointsSpent(talents = {}) {
  const normalized = normalizeTrainerTalents(talents);
  return TRAINER_TALENT_KEYS.reduce((sum, key) => sum + normalized[key], 0);
}

export function killXpBonusForDefeats(defeats = 0) {
  const safeDefeats = Math.max(0, Math.floor(Number(defeats) || 0));
  const steps = Math.floor(safeDefeats / KILLS_PER_XP_BONUS_STEP);
  return Math.min(MAX_KILL_XP_BONUS, steps * XP_BONUS_PER_KILL_STEP);
}

export function trainerPerks(level = 1, prestige = 0, talents = {}) {
  const safeLevel = Math.max(1, Math.min(300, Math.floor(Number(level) || 1)));
  const safePrestige = Math.max(0, Math.min(MAX_TRAINER_PRESTIGE, Math.floor(Number(prestige) || 0)));
  const ranks = normalizeTrainerTalents(talents);
  return Object.freeze({
    talents: ranks,
    // Só o parceiro equipado ganha EXP; Mentor deixou de repartir com a reserva e
    // passou a ser o talento que faz esse parceiro treinar offline.
    offlineTrainingPct: ranks.trainer * OFFLINE_POKEMON_PCT_PER_RANK,
    lootMultiplier: 1 + ranks.hunter * 0.03,
    captureMultiplier: 1 + ranks.catcher * 0.02,
    researchMultiplier: 1 + ranks.researcher * 0.05,
    xpMultiplier: 1 + safePrestige * 0.02,
    offlineCapHours: Math.min(20, 4 + Math.floor(safeLevel / 100) * 4 + safePrestige),
    masteryUnlocked: safeLevel >= 150,
    prestigeAvailable: safeLevel >= 300 && safePrestige < MAX_TRAINER_PRESTIGE,
  });
}

/** RelÃ³gio de iniciativa: Speed reduz o intervalo, com limites legÃ­veis. */
export function combatIntervalMs(speed = 1) {
  const safeSpeed = Math.max(1, Number(speed) || 1);
  return Math.round(clamp(1200 / (1 + safeSpeed / 160), 450, 1200));
}

/** Dificuldades explícitas do Atlas (offset sobre o nível da faixa de hunt). */
export const HUNT_DIFFICULTIES = Object.freeze([0, 3, 5]);

/**
 * Nível de encontro: nível base da hunt (faixa) + dificuldade escolhida,
 * limitado à faixa da espécie. Zonas 300+ não usam esta função.
 */
export function encounterLevel(speciesOrMinMax, huntBaseLevel, difficulty = 0) {
  const base = Math.max(1, Math.floor(Number(huntBaseLevel) || 1));
  const diff = HUNT_DIFFICULTIES.includes(Number(difficulty))
    ? Number(difficulty)
    : clamp(Math.floor(Number(difficulty) || 0), 0, 5);
  let min = 1;
  let max = 300;
  if (speciesOrMinMax && typeof speciesOrMinMax === 'object') {
    min = Math.max(1, Math.floor(Number(speciesOrMinMax.minLevel ?? speciesOrMinMax.min) || 1));
    max = Math.max(min, Math.floor(Number(speciesOrMinMax.maxLevel ?? speciesOrMinMax.max) || 300));
  } else if (typeof speciesOrMinMax === 'string' || typeof speciesOrMinMax === 'number') {
    min = speciesMinRouteLevel(speciesOrMinMax) || 1;
    max = 300;
  }
  return clamp(base + diff, min, max);
}

export function combatRiskMultiplier(playerLevel, wildLevel) {
  const gap = Math.max(0, Math.floor(Number(wildLevel) || 1) - Math.floor(Number(playerLevel) || 1));
  // Retune PLANO-MESTRE / PROPOSTA Fix 2: +8%/nível, teto +40% em 5 níveis.
  return 1 + Math.min(5, gap) * 0.08;
}

export function combatXpForLevel(wildLevel, entry = null, bonus = 0, playerLevel = wildLevel) {
  const normalizedWildLevel = Math.max(1, Number(wildLevel) || 1);
  const base = normalizedWildLevel * 8 + 12;
  const difficulty = entry ? clamp(Number(entry.bst || 420) / 420, 0.82, 1.32) : 1;
  const risk = combatRiskMultiplier(playerLevel, normalizedWildLevel);
  return Math.max(1, Math.round((base * difficulty + (Number(bonus) || 0)) * risk));
}

/**
 * Multiplicador de raridade nos stats de combate.
 * α = 1: o número exibido (×3,2 Ancião, ×4 Secreto) é o do combate —
 * igual ao Poke Idle World (Quality entra cheia nos stats).
 */
export function softenedRarityStatMultiplier(displayMultiplier = 1) {
  const mult = Number(displayMultiplier);
  return Number.isFinite(mult) && mult > 0 ? mult : 1;
}

export function progressionHours({ from = 1, to, xpToNext, wildLevelFor, defeatsPerHour = PROGRESSION_TARGETS.expectedDefeatsPerHour }) {
  let kills = 0;
  for (let level = from; level < to; level++) {
    const reward = combatXpForLevel(wildLevelFor(level));
    kills += xpToNext(level) / reward;
  }
  return kills / defeatsPerHour;
}
